import { useEffect, useRef } from 'react';
import { Stack, TextFieldProps } from '@mui/material';
import { useApiIsLoaded, useMapsLibrary } from '@vis.gl/react-google-maps';
import { FieldHookConfig, useField, useFormikContext } from 'formik';

import { FormLabel } from '../label/FormLabel';

import { CurrentNearByAddressField } from './CurrentNearByAddressField';
import { SelectedAddress } from './SelectedAddress';

import { SearchIcon } from '~assets/icons';
import { AutoCompleteGoogleMapsWithDetail } from '~components/atoms';
import { usePinningStepContext } from '~providers/PinningStepProvider';
import { FilterLocationOption } from '~types';

type Props = {
  label?: React.ReactNode;
  placeholder?: string;
  customError?: string;
  textFieldProps?: TextFieldProps;
  // Extra address shown
  addressInfo?: string;
  isRemoveAddress?: boolean;
} & FieldHookConfig<FilterLocationOption | null>;

export const AddressField: React.FC<Props> = ({
  label,
  placeholder = 'Search',
  customError,

  addressInfo,
  isRemoveAddress,
  ...props
}) => {
  const isLoaded = useApiIsLoaded();
  const placesLib = useMapsLibrary('places');
  const { handleSetSelectedGooglePlaceId } = usePinningStepContext();

  const fakeMapRef = useRef<HTMLDivElement | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null,
  );
  const mapRef = useRef<google.maps.Map | null>(null);
  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);

  /**
   * initial autocompleteService
   */
  useEffect(() => {
    if (!isLoaded || !placesLib) return;
    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
    }
  }, [isLoaded, placesLib]);

  /**
   * initial placesServiceRef for getDetails address
   * (lat, lng, state, country, country code, zip, city)
   */
  useEffect(() => {
    if (!fakeMapRef.current) return;
    if (!isLoaded || !placesLib) return;

    if (!mapRef.current) {
      mapRef.current = new window.google.maps.Map(fakeMapRef.current);
    }
    if (!placesServiceRef.current) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        mapRef.current,
      );
    }
  }, [fakeMapRef, isLoaded, placesLib]);

  const [{ name, onBlur }, { touched, error }] =
    useField<FilterLocationOption | null>(props);

  const { setFieldValue, setFieldTouched } = useFormikContext();

  const onChangeDetailAddress = (
    fieldName: string,
    value?: string | number,
  ) => {
    setFieldValue(fieldName, value ?? '');
    setTimeout(() => {
      setFieldTouched(fieldName, true);
    }, 100);
  };
  const onChangeAddress = (value: FilterLocationOption | null) => {
    onChangeDetailAddress('venue.address.id', value?.id);
    onChangeDetailAddress('venue.address.name', value?.name);
    onChangeDetailAddress('venue.address.location.lat', value?.location.lat);
    onChangeDetailAddress('venue.address.location.lng', value?.location.lng);
    onChangeDetailAddress(
      'venue.address.location.infoAddress.country',
      value?.location.infoAddress.country,
    );
    onChangeDetailAddress(
      'venue.address.location.infoAddress.countryCode',
      value?.location.infoAddress.countryCode,
    );
    onChangeDetailAddress(
      'venue.address.location.infoAddress.city',
      value?.location.infoAddress.city,
    );
    onChangeDetailAddress(
      'venue.address.location.infoAddress.state',
      value?.location.infoAddress.state,
    );
    onChangeDetailAddress(
      'venue.address.location.infoAddress.zip',
      value?.location.infoAddress.zip,
    );
    handleSetSelectedGooglePlaceId(value?.id || undefined);
  };

  const onRemoveAddress = () => {
    onChangeAddress(null);
  };

  return (
    <>
      {label && <FormLabel label={label} />}

      <AutoCompleteGoogleMapsWithDetail
        fakeMapRef={fakeMapRef}
        autocompleteServiceRef={autocompleteServiceRef}
        placesServiceRef={placesServiceRef}
        onSelect={onChangeAddress}
        textFieldProps={{
          placeholder,
          name,
          onBlur,
          error: !!(touched && (customError ?? error)),
          helperText: touched && (customError ?? error),
          InputProps: {
            startAdornment: (
              <Stack marginLeft={1} marginRight={0.5}>
                <SearchIcon />
              </Stack>
            ),
            sx: {
              padding: '5px 0 !important',
              borderRadius: '100px',
            },
          },
          inputProps: {
            autoComplete: 'off',
            style: {
              width: '75%',
              flexGrow: 'unset',
            },
          },
          ...props.textFieldProps,
        }}
      />

      {addressInfo && (
        <SelectedAddress
          onRemove={isRemoveAddress ? onRemoveAddress : undefined}
          address={addressInfo}
        />
      )}
      <CurrentNearByAddressField
        onChangeCurrentLocation={onChangeAddress}
        placesServiceRef={placesServiceRef}
      />
    </>
  );
};
